import { Radio, RadioField, RadioGroup } from '../../catalyst/radio';
import { Checkbox, CheckboxField, CheckboxGroup } from '../../catalyst/checkbox';
import { Label } from '../../catalyst/fieldset';
import AuditLogActionLabel from '../../components/AuditLogActionLabel';
import { SERIES_COLORS, type AuditFilters } from './auditFilters.types';

interface AuditFiltersProps {
  filters: AuditFilters;
  setFilters: (filters: AuditFilters) => void;
  /** Chart series values (inner dimension): usernames when groupBy=username, actions when groupBy=action */
  seriesSet: string[];
  /** Summed values (outer dimension): actions when groupBy=username, usernames when groupBy=action */
  sumSet: string[];
}

export default function AuditFiltersPanel({ filters, setFilters, seriesSet, sumSet }: AuditFiltersProps) {
  const { groupBy } = filters;

  // The chart endpoint swaps dimensions with group_by, so each checkbox list must
  // write to the store matching what it actually displays — these exclusions are
  // persisted and re-applied to the audit table as excludeUsernames/excludeActions.
  const seriesKey = groupBy === 'action' ? 'deselectedActions' : 'deselectedUsers';
  const sumKey = groupBy === 'action' ? 'deselectedUsers' : 'deselectedActions';
  const seriesDeselected = filters[seriesKey];
  const sumDeselected = filters[sumKey];

  const handleGroupByChange = (value: string) => {
    // Clear both stores — persisted deselections from the previous mode belong to
    // the other dimension and would exclude the wrong rows from the table.
    setFilters({
      ...filters,
      groupBy: value as AuditFilters['groupBy'],
      deselectedUsers: [],
      deselectedActions: [],
    });
  };

  const handleSeriesToggle = (value: string, checked: boolean) => {
    setFilters({
      ...filters,
      [seriesKey]: checked
        ? seriesDeselected.filter((v) => v !== value)
        : (() => {
            const next = [...seriesDeselected, value];
            return next.length === seriesSet.length ? [] : next;
          })(),
    });
  };

  const handleSeriesSolo = (value: string) => {
    const isSolo = seriesDeselected.length === seriesSet.length - 1 && !seriesDeselected.includes(value);
    setFilters({
      ...filters,
      [seriesKey]: isSolo ? [] : seriesSet.filter((v) => v !== value),
    });
  };

  const handleSumToggle = (value: string, checked: boolean) => {
    setFilters({
      ...filters,
      [sumKey]: checked
        ? sumDeselected.filter((v) => v !== value)
        : (() => {
            const next = [...sumDeselected, value];
            return next.length === sumSet.length ? [] : next;
          })(),
    });
  };

  const handleSumSolo = (value: string) => {
    const isSolo = sumDeselected.length === sumSet.length - 1 && !sumDeselected.includes(value);
    setFilters({
      ...filters,
      [sumKey]: isSolo ? [] : sumSet.filter((v) => v !== value),
    });
  };

  const renderLabel = (value: string, isAction: boolean) =>
    isAction ? <AuditLogActionLabel action={value} /> : value;

  return (
    <div className="flex flex-col gap-5">
      {/* Group By */}
      <div>
        <p className="text-xs font-medium text-gray-500 dark:text-zinc-400 mb-2 uppercase tracking-wide">
          Group By
        </p>
        <RadioGroup value={groupBy} onChange={handleGroupByChange} className="!space-y-2">
          <RadioField>
            <Radio value="username" />
            <Label className="!text-xs">User</Label>
          </RadioField>
          <RadioField>
            <Radio value="action" />
            <Label className="!text-xs">Action</Label>
          </RadioField>
        </RadioGroup>
      </div>

      {/* Group (chart series) */}
      {seriesSet.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-zinc-400 mb-2 uppercase tracking-wide">
            Group
          </p>
          <CheckboxGroup className="!space-y-2">
            {seriesSet.map((value, idx) => (
              <div key={value} className="flex items-center gap-2">
                <CheckboxField>
                  <Checkbox
                    checked={!seriesDeselected.includes(value)}
                    onChange={(checked) => handleSeriesToggle(value, checked)}
                  />
                  <Label className="!text-xs">{renderLabel(value, groupBy === 'action')}</Label>
                </CheckboxField>
                <span
                  className="inline-block size-2.5 rounded-full shrink-0 cursor-pointer"
                  title="Show only this series"
                  style={{ backgroundColor: SERIES_COLORS[idx % SERIES_COLORS.length] }}
                  onClick={() => handleSeriesSolo(value)}
                />
              </div>
            ))}
          </CheckboxGroup>
        </div>
      )}

      {/* Sum (the other dimension) */}
      {sumSet.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-zinc-400 mb-2 uppercase tracking-wide">
            Sum
          </p>
          <CheckboxGroup className="!space-y-2">
            {sumSet.map((value) => (
              <div key={value} className="flex items-center gap-2">
                <CheckboxField>
                  <Checkbox
                    checked={!sumDeselected.includes(value)}
                    onChange={(checked) => handleSumToggle(value, checked)}
                  />
                  <Label className="!text-xs">{renderLabel(value, groupBy === 'username')}</Label>
                </CheckboxField>
                <button
                  type="button"
                  className="text-xs text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300 cursor-pointer"
                  title="Show only this one"
                  onClick={() => handleSumSolo(value)}
                >
                  only
                </button>
              </div>
            ))}
          </CheckboxGroup>
        </div>
      )}
    </div>
  );
}
